import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  DivineServiceApiService as CoreDivineServiceApiService,
  BulkAttendanceDto as GeneratedBulkAttendanceDto,
  OrganizationUnitDto,
} from '@core/api/services/divine-service-api.service';
import { CreateDivineServiceRequest, UpdateDivineServiceRequest } from '@core/api/generated/api-client';
import { CatalogApiService } from '@core/api/services/catalog-api.service';
import {
  DivineServiceDetailDto,
  CreateDivineServiceDto,
  UpdateDivineServiceDto,
  DivineServicePagedResponse,
  AttendanceResponseDto,
  MembersAttendancePagedResponse,
  DivineServiceTypeDto,
  CreateOrUpdateAttendanceDto,
} from '../../models/divine-service.model';

/**
 * Feature-level API service for divine services.
 * Delegates to the NSwag-generated DivineServiceClient via the core wrapper.
 * Maintains the legacy method signatures used by feature components and pages.
 */
@Injectable({
  providedIn: 'root'
})
export class DivineServiceApiService {
  constructor(
    private readonly coreService: CoreDivineServiceApiService,
    private readonly catalogService: CatalogApiService,
  ) {}

  // ========== DIVINE SERVICES - LIST & CRUD ==========

  /**
   * Obtiene una lista paginada de servicios divinos con filtros opcionales.
   * NOTE: param order differs from generated client — page/pageSize come first here.
   */
  getDivineServices(
    page: number = 1,
    pageSize: number = 10,
    unitId?: string,
    startDate?: string,
    endDate?: string,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc',
    searchTerm?: string,
    divineServiceTypeId?: number,
  ): Observable<DivineServicePagedResponse> {
    return this.coreService.getDivineServices(
      unitId ?? null,
      startDate ?? null,
      endDate ?? null,
      sortBy ?? null,
      sortDirection ?? null,
      page,
      pageSize,
      searchTerm ?? null,
      divineServiceTypeId ?? null,
    ) as unknown as Observable<DivineServicePagedResponse>;
  }

  /**
   * Obtiene los detalles completos de un servicio divino.
   */
  getDivineService(id: string): Observable<DivineServiceDetailDto> {
    return this.coreService.getDivineServiceById(id) as unknown as Observable<DivineServiceDetailDto>;
  }

  /**
   * Crea un nuevo servicio divino.
   */
  createDivineService(dto: CreateDivineServiceDto): Observable<DivineServiceDetailDto> {
    const request = new CreateDivineServiceRequest(dto as any);
    return this.coreService.createDivineService(request) as unknown as Observable<DivineServiceDetailDto>;
  }

  /**
   * Actualiza un servicio divino existente.
   */
  updateDivineService(id: string, dto: UpdateDivineServiceDto): Observable<DivineServiceDetailDto> {
    const request = new UpdateDivineServiceRequest(dto as any);
    return this.coreService.updateDivineService(id, request) as unknown as Observable<DivineServiceDetailDto>;
  }

  /**
   * Elimina un servicio divino.
   */
  deleteDivineService(id: string): Observable<void> {
    return this.coreService.deleteDivineService(id);
  }

  closeDivineService(id: string): Observable<void> {
    return this.coreService.closeDivineService(id);
  }

  // ========== ATTENDANCE ==========

  /**
   * Obtiene TODOS los miembros de la unidad con información de asistencia.
   */
  getMembersWithAttendance(
    divineServiceId: string,
    page: number = 1,
    pageSize: number = 100,
  ): Observable<MembersAttendancePagedResponse> {
    return this.coreService.getMembersWithAttendance(divineServiceId, page, pageSize) as unknown as Observable<MembersAttendancePagedResponse>;
  }

  /**
   * Obtiene la asistencia a un servicio divino específico (LEGACY).
   */
  getAttendance(
    divineServiceId: string,
    page: number = 1,
    pageSize: number = 100,
  ): Observable<AttendanceResponseDto> {
    return this.coreService.getAttendance(divineServiceId, page, pageSize) as unknown as Observable<AttendanceResponseDto>;
  }

  /**
   * Marca asistencia de un miembro a un servicio (UPSERT).
   * The dto parameter is accepted for backwards compatibility but is not sent to the backend.
   */
  markAttendance(
    divineServiceId: string,
    memberId: string,
    _dto?: CreateOrUpdateAttendanceDto,
  ): Observable<void> {
    return this.coreService.markAttendance(divineServiceId, memberId);
  }

  /**
   * Desmarca asistencia de un miembro a un servicio.
   */
  unmarkAttendance(divineServiceId: string, memberId: string): Observable<void> {
    return this.coreService.unmarkAttendance(divineServiceId, memberId);
  }

  /**
   * Marca asistencia de múltiples personas a un servicio (operación bulk).
   */
  bulkMarkAttendance(divineServiceId: string, personIds: string[]): Observable<void> {
    const dto = new GeneratedBulkAttendanceDto({ personIds });
    return this.coreService.bulkMarkAttendance(divineServiceId, dto);
  }

  /**
   * Desmarca asistencia de múltiples personas a un servicio (operación bulk).
   */
  bulkUnmarkAttendance(divineServiceId: string, personIds: string[]): Observable<void> {
    const dto = new GeneratedBulkAttendanceDto({ personIds });
    return this.coreService.bulkUnmarkAttendance(divineServiceId, dto);
  }

  // ========== CATALOG ==========

  /**
   * Obtiene todos los tipos de servicios divinos disponibles.
   */
  getDivineServiceTypes(): Observable<DivineServiceTypeDto[]> {
    return this.catalogService.getDivineServiceTypes() as unknown as Observable<DivineServiceTypeDto[]>;
  }

  /**
   * Devuelve las unidades en las que el usuario actual puede crear/editar/eliminar servicios divinos.
   */
  getManageableUnits(): Observable<OrganizationUnitDto[]> {
    return this.coreService.getManageableUnits();
  }
}
